import { memo, useMemo, useState } from 'react';
import { ModalBody, ModalContent, Modal, ModalHeader, ModalFooter } from '@heroui/react';
import { OnChangeFn, PaginationState } from '@tanstack/react-table';
import { useGetAdvanceableStudentsQuery, AdvanceableStudent } from '@/entities/Courses';
import { ContainerTable } from '@/shared/ui/AppTable';
import { AppButton } from '@/shared/ui/AppButton';
import { SectionLoader } from '@/shared/ui/Loader';
import { scaleInOut } from '@/shared/lib/utils/modalTransitions';
import { advanceableStudentsColumns } from '../../../config/advanceableStudentsColumns';
import styles from './TransferToNextTermModal.module.scss';

interface TransferToNextTermModalProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  intakeId: number;
  groupId?: number;
  fromTermNumber: number;
  onSuccess?: () => void;
}

export const TransferToNextTermModal = memo(
  ({
    isOpen,
    onOpenChange,
    intakeId,
    groupId,
    fromTermNumber,
    onSuccess,
  }: TransferToNextTermModalProps) => {
    const [pagination, setPagination] = useState<PaginationState>({
      pageIndex: 0,
      pageSize: 10,
    });

    const { data, isLoading } = useGetAdvanceableStudentsQuery(
      {
        intakeId,
        group: groupId,
        fromTermNumber,
      },
      { skip: !isOpen },
    );

    const students = useMemo(() => data?.data?.enrollments || [], [data?.data?.enrollments]);
    const nextTermNumber = data?.data?.nextTermNumber || 0;

    const handleClose = () => {
      onOpenChange(false);
    };

    const handleTransfer = () => {
      // TODO: Implement transfer to next term
      onSuccess?.();
      handleClose();
    };

    return (
      <Modal
        isOpen={isOpen}
        onOpenChange={handleClose}
        placement="center"
        motionProps={scaleInOut}
        size="5xl"
        backdrop="opaque"
        scrollBehavior="inside"
        classNames={{
          base: 'p-6 flex flex-col gap-5',
          closeButton: styles.closeBtn,
        }}
      >
        <ModalContent>
          {() => (
            <>
              <ModalHeader className={styles.title}>Transfer to the next term</ModalHeader>
              <ModalBody className="p-0 flex flex-col gap-4">
                {isLoading ? (
                  <SectionLoader />
                ) : (
                  <ContainerTable<AdvanceableStudent>
                    data={students}
                    columns={advanceableStudentsColumns()}
                    pagination={pagination}
                    setPagination={setPagination as OnChangeFn<PaginationState>}
                    hideTopActions={true}
                  />
                )}
              </ModalBody>
              <ModalFooter className="flex justify-center items-center gap-4 p-0 pt-6">
                <AppButton fullWidth color="black" onPress={handleTransfer}>
                  To the next term
                </AppButton>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    );
  },
);

TransferToNextTermModal.displayName = 'TransferToNextTermModal';

